import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { CalendarX2, Download } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { WithTooltip } from '@/components/with-tooltip';
import { cn } from '@/lib/utils';

type CampaignsBulkActionsPopoverLabels = {
  selected: (arg: { count: number }) => string;
  export: string;
  exporting: string;
  delete: string;
  deleting: string;
  confirmTitle: string;
  confirmDescription: (arg: { count: number }) => string;
  confirmAction: string;
  cancel: string;
  disabledTooltip: string;
};

export function CampaignsBulkActionsPopover({
  selectedCount,
  canDelete,
  isDeleting,
  isExporting,
  labels,
  onDelete,
  onExport,
}: {
  selectedCount: number;
  canDelete: boolean;
  isDeleting: boolean;
  isExporting: boolean;
  labels: CampaignsBulkActionsPopoverLabels;
  onDelete: () => void;
  onExport: () => void;
}) {
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [exportConfirmOpen, setExportConfirmOpen] = useState(false);
  const isVisible = selectedCount > 0;

  return (
    <>
      <div
        className={cn(
          'fixed left-1/2 top-4 z-50 -translate-x-1/2 transition-all duration-200',
          isVisible
            ? 'translate-y-0 opacity-100'
            : '-translate-y-4 opacity-0 pointer-events-none'
        )}
      >
        <div className="flex items-center gap-3 rounded-md border bg-background/95 px-4 py-2 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <span className="text-sm font-medium">
            {labels.selected({ count: selectedCount })}
          </span>
          <Button
            size="icon"
            variant="outline"
            className="h-8 w-8"
            onClick={() => setExportConfirmOpen(true)}
            disabled={isExporting}
            aria-label={labels.export}
          >
            <Download className="h-4 w-4" />
            <span className="sr-only">
              {isExporting ? labels.exporting : labels.export}
            </span>
          </Button>
          <WithTooltip
            tooltip={labels.disabledTooltip}
            disabled={canDelete || isDeleting}
          >
            <span className="inline-flex">
              <Button
                size="icon"
                variant="destructive"
                className="h-8 w-8"
                onClick={() => setCancelConfirmOpen(true)}
                disabled={!canDelete || isDeleting}
                aria-label={labels.delete}
              >
                <CalendarX2 className="h-4 w-4" />
                <span className="sr-only">
                  {isDeleting ? labels.deleting : labels.delete}
                </span>
              </Button>
            </span>
          </WithTooltip>
        </div>
      </div>

      <AlertDialog open={exportConfirmOpen} onOpenChange={setExportConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{labels.export}</AlertDialogTitle>
            <AlertDialogDescription>
              {labels.selected({ count: selectedCount })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{labels.cancel}</AlertDialogCancel>
            <AlertDialogAction onClick={onExport} disabled={isExporting}>
              {isExporting ? labels.exporting : labels.export}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={cancelConfirmOpen} onOpenChange={setCancelConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{labels.confirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {labels.confirmDescription({ count: selectedCount })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{labels.cancel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? labels.deleting : labels.confirmAction}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
